import React, { useState, useEffect, useRef } from "react";
import { useLoginModal } from "../components/LoginModalProvider";
import { useNavigate, useParams } from "react-router-dom";
import {
  MapPin, Calendar, Clock, MessageSquare, Phone, User,
  ShieldCheck, Star, ChevronLeft, CheckCircle2, AlertCircle,
  CreditCard, FileText, Camera, HelpCircle, RefreshCw,
  Loader2, ChevronRight, X, Award, Zap, Gift, Download,
  Image as ImageIcon, RotateCcw,
} from "lucide-react";
import { supabase } from "../lib/supabase";
import CancelBookingModal from "../components/CancelBookingModal";
import RescheduleBookingModal from "../components/RescheduleBookingModal";

/* ─────────────────────────────────────────────
   DESIGN TOKENS
───────────────────────────────────────────── */
const T = {
  green:       "#0F7B3E",
  greenDark:   "#0C6231",
  greenTint:   "#E8F5E9",
  greenBd:     "#C8E6C9",
  pageBg:      "#F1F3F6",
  white:       "#FFFFFF",
  card:        "#FFFFFF",
  card2:       "#F8F9FB",
  border:      "#E8EAED",
  borderFaint: "#F0F2F5",
  text:        "#0D1117",
  textMid:     "#374151",
  sub:         "#6B7280",
  faint:       "#9CA3AF",
  ghost:       "#C4C9D4",
  amber:       "#D97706",
  amberTint:   "#FFFBEB",
  amberBd:     "#FCD34D",
  blue:        "#1D4ED8",
  blueTint:    "#EFF6FF",
  blueBd:      "#BFDBFE",
  red:         "#DC2626",
  redTint:     "#FEF2F2",
  redBd:       "#FECACA",
  font: "'Plus Jakarta Sans', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
  r8: "8px", r10: "10px", r12: "12px", r14: "14px",
  r16: "16px", r20: "20px", r24: "24px", rFull: "999px",
  s1: "0 1px 3px rgba(0,0,0,0.05), 0 1px 2px rgba(0,0,0,0.03)",
  s2: "0 4px 16px rgba(0,0,0,0.07), 0 1px 4px rgba(0,0,0,0.04)",
  s3: "0 8px 32px rgba(0,0,0,0.09), 0 2px 8px rgba(0,0,0,0.05)",
  sGreen: "0 4px 20px rgba(15,123,62,0.22)",
};

/* ─────────────────────────────────────────────
   JOURNEY  — "On The Way" label
───────────────────────────────────────────── */
const JOURNEY = [
  { key: "confirmed",   label: "Confirmed",   states: ["confirmed","pro_assigned","accepted"] },
  { key: "en_route",    label: "On The Way",  states: ["on_the_way"] },
  { key: "arrived",     label: "Arrived",     states: ["arrived"] },
  { key: "in_progress", label: "In Progress", states: ["in_progress"] },
  { key: "completed",   label: "Complete",    states: ["completed"] },
];

function getJourneyIndex(status) {
  for (let i = 0; i < JOURNEY.length; i++) {
    if (JOURNEY[i].states.includes(status)) return i;
  }
  return 0;
}

const STATUS_CFG = {
  confirmed:    { label: "Confirmed",   color: T.blue,    bg: T.blueTint,  bd: T.blueBd,  dot: "#3B82F6" },
  pro_assigned: { label: "Confirmed",   color: T.blue,    bg: T.blueTint,  bd: T.blueBd,  dot: "#3B82F6" },
  accepted:     { label: "Confirmed",   color: T.blue,    bg: T.blueTint,  bd: T.blueBd,  dot: "#3B82F6" },
  on_the_way:   { label: "On The Way",  color: "#0369A1", bg: "#F0F9FF",   bd: "#BAE6FD", dot: "#0EA5E9" },
  arrived:      { label: "Arrived",     color: T.green,   bg: T.greenTint, bd: T.greenBd, dot: T.green   },
  in_progress:  { label: "In Progress", color: "#065F46", bg: "#ECFDF5",   bd: "#A7F3D0", dot: "#10B981" },
  completed:    { label: "Completed",   color: T.green,   bg: T.greenTint, bd: T.greenBd, dot: T.green   },
  cancelled:    { label: "Cancelled",   color: T.red,     bg: T.redTint,   bd: T.redBd,   dot: T.red     },
};

const INFO_CARDS_BY_STATUS = {
  default: [
    {
      icon: "zap", bg: "#FFFBEB", bd: "#FCD34D", tc: "#92400E",
      title: "While You Wait",
      lines: ["Clear access paths to areas being serviced","Ensure someone is home to let the professional in","Keep your phone nearby for any updates"],
    },
    {
      icon: "shield", bg: T.greenTint, bd: T.greenBd, tc: T.greenDark,
      title: "Jirani Guarantee",
      lines: ["All professionals are identity-verified","Background-checked before joining the platform","Not satisfied? Our team resolves issues fast"],
    },
    {
      icon: "award", bg: "#F5F3FF", bd: "#DDD6FE", tc: "#4C1D95",
      title: "Did You Know?",
      lines: ["Only the top 15% of applicants are accepted","Every service is backed by our guarantee","Average Jirani professional rating is 4.8 stars"],
    },
  ],
  on_the_way: [
    {
      icon: "zap", bg: "#FFFBEB", bd: "#FCD34D", tc: "#92400E",
      title: "Getting Ready",
      lines: ["Secure pets so the professional can work safely","Clear access to the areas being serviced","Have the gate code or key ready if needed"],
    },
    {
      icon: "shield", bg: T.greenTint, bd: T.greenBd, tc: T.greenDark,
      title: "Jirani Guarantee",
      lines: ["All professionals are identity-verified","Background-checked before joining the platform","Not satisfied? Our team resolves issues fast"],
    },
    {
      icon: "award", bg: "#F5F3FF", bd: "#DDD6FE", tc: "#4C1D95",
      title: "Did You Know?",
      lines: ["Only the top 15% of applicants are accepted","Every service is backed by our guarantee","Average Jirani professional rating is 4.8 stars"],
    },
  ],
  in_progress: [
    {
      icon: "zap", bg: "#FFFBEB", bd: "#FCD34D", tc: "#92400E",
      title: "Service In Progress",
      lines: ["Feel free to go about your day — the pro has it covered","Check in if you want to give feedback or guidance","Let the professional know if priorities change"],
    },
    {
      icon: "shield", bg: T.greenTint, bd: T.greenBd, tc: T.greenDark,
      title: "Jirani Guarantee",
      lines: ["You are covered if anything falls short","Raise a dispute within 48 hours of completion","Our support team responds within 2 hours"],
    },
    {
      icon: "award", bg: "#F5F3FF", bd: "#DDD6FE", tc: "#4C1D95",
      title: "Did You Know?",
      lines: ["Only the top 15% of applicants are accepted","Every service is backed by our guarantee","Average Jirani professional rating is 4.8 stars"],
    },
  ],
};
const INFO_CARDS = INFO_CARDS_BY_STATUS.default;

const SEED = {
  id: "ORD-2026-8841",
  status: "on_the_way",
  service: "Deep Cleaning",
  date: "Saturday, June 13",
  time: "9:00 AM",
  duration: "3 hours",
  address: "14 Riverside Drive, Westlands, Nairobi",
  propertyType: "2-Bedroom Apartment",
  price: 6500,
  currency: "KES",
  paymentMethod: "M-Pesa",
  paymentStatus: "paid",
  specialNotes: "Please ring the bell twice. Gate code is 2418.",
  pro: {
    name: "Sarah Wanjiku", initials: "SW",
    avatar_url: "https://i.pravatar.cc/150?u=sarah_wanjiku_jirani",
    rating: 4.9, reviewCount: 246, jobsCompleted: 312,
    yearsExperience: 4, responseRate: 98, verified: true,
    memberSince: "2022",
    specialization: "Premium residential cleaning",
    responseTime: "Under 5 minutes",
  },
};

/* ─────────────────────────────────────────────
   RECEIPT GENERATOR
   Builds a premium PDF receipt using jsPDF
   (loaded dynamically from cdnjs)
───────────────────────────────────────────── */
async function downloadReceipt(booking) {
  if (!window.jspdf) {
    await new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js";
      s.onload = resolve; s.onerror = reject;
      document.head.appendChild(s);
    });
  }
  const { jsPDF } = window.jspdf;
  const doc = new jsPDF({ unit:"pt", format:"a4" });
  const PW = doc.internal.pageSize.getWidth();
  const PH = doc.internal.pageSize.getHeight();
  const LM = 48, RM = 48;
  const CW = PW - LM - RM;

  // ── colour helpers ────────────────────────────────────
  const hexRgb = h => [parseInt(h.slice(1,3),16), parseInt(h.slice(3,5),16), parseInt(h.slice(5,7),16)];
  const F = h => doc.setFillColor(...hexRgb(h));
  const S = h => doc.setDrawColor(...hexRgb(h));
  const TX = h => doc.setTextColor(...hexRgb(h));

  const GREEN      = "#0F7B3E";
  const GREEN_DARK = "#0B6232";
  const GREEN_TINT = "#E9F5EE";
  const GREEN_BD   = "#C3DFD0";
  const DARK       = "#0D1117";
  const SUB        = "#6B7280";
  const FAINT      = "#9CA3AF";
  const RULE       = "#E8EAED";
  const ROW_A      = "#F8FAFB";
  const WHITE      = "#FFFFFF";
  const ROW_H      = 24;

  // ── watermark ─────────────────────────────────────────
  doc.setGState(new doc.GState({ opacity: 0.04 }));
  F(GREEN); TX(GREEN);
  doc.setFont("helvetica","bold"); doc.setFontSize(64);
  for (let row = -1; row < 6; row++) {
    for (let col = -1; col < 4; col++) {
      doc.saveGraphicsState();
      doc.text("JIRANI", LM + col * 220, 120 + row * 200, { angle: 38 });
      doc.restoreGraphicsState();
    }
  }
  doc.setGState(new doc.GState({ opacity: 1 }));

  // ── load Playfair Display for wordmark ──────────────────
  await new Promise((resolve, reject) => {
    if (document.head.querySelector('link[data-font="playfair-receipt"]')) { resolve(); return; }
    const l = document.createElement("link");
    l.rel = "stylesheet";
    l.href = "https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600&display=swap";
    l.setAttribute("data-font", "playfair-receipt");
    l.onload = resolve; l.onerror = resolve; // non-fatal if offline
    document.head.appendChild(l);
  });
  // Give browser a tick to parse the font
  await new Promise(r => setTimeout(r, 120));

  // ── header band ───────────────────────────────────────
  const HEADER_H = 90;
  F(GREEN); doc.rect(0, 0, PW, HEADER_H, "F");
  F(GREEN_DARK); doc.rect(0, HEADER_H - 3, PW, 3, "F");

  // Wordmark: "JiraniApp" in Playfair Display 600
  TX(WHITE);
  doc.setFont("playfair display", "normal"); // jsPDF lowercases font names
  doc.setFontSize(26);
  const wordmarkY = 48;
  doc.text("JiraniApp", LM, wordmarkY);
  const wordW = doc.getTextWidth("JiraniApp");

  // Green dot after wordmark
  F("#34D980");
  doc.circle(LM + wordW + 7, wordmarkY - 3, 5, "F");

  // Tagline
  TX("#A7F3D0"); doc.setFont("helvetica","normal"); doc.setFontSize(8.5);
  doc.text("Home Services  |  Verified Professionals", LM, wordmarkY + 14);

  // Right: receipt label
  TX(WHITE); doc.setFont("helvetica","bold"); doc.setFontSize(10);
  doc.text("SERVICE RECEIPT", PW - RM, 36, { align:"right" });
  TX("#A7F3D0"); doc.setFont("helvetica","normal"); doc.setFontSize(8.5);
  doc.text(booking.id, PW - RM, 50, { align:"right" });
  const today = new Date().toLocaleDateString("en-KE",{day:"numeric",month:"long",year:"numeric"});
  doc.setFontSize(8);
  doc.text(today, PW - RM, 63, { align:"right" });

  // ── hero card ─────────────────────────────────────────
  let y = HEADER_H + 20;
  const CARD_H = 56;
  F(GREEN_TINT); S(GREEN_BD); doc.setLineWidth(0.6);
  doc.roundedRect(LM, y, CW, CARD_H, 8, 8, "FD");

  TX(GREEN_DARK); doc.setFont("helvetica","bold"); doc.setFontSize(14);
  doc.text(booking.service, LM + 14, y + 22);

  TX(SUB); doc.setFont("helvetica","normal"); doc.setFontSize(8.5);
  const heroSub = `${booking.propertyType || "Residential"}  |  ${booking.date}  |  ${booking.time}  |  ${booking.duration}`;
  doc.text(heroSub, LM + 14, y + 38);

  // PAID pill
  const PW2 = 46, PH2 = 18;
  const px = PW - RM - PW2 - 8, py = y + (CARD_H - PH2) / 2;
  F(GREEN); doc.roundedRect(px, py, PW2, PH2, 5, 5, "F");
  TX(WHITE); doc.setFont("helvetica","bold"); doc.setFontSize(8.5);
  doc.text("PAID", px + PW2 / 2, py + 12, { align:"center" });

  // ── section helper ────────────────────────────────────
  const sHead = (label, sy) => {
    TX(SUB); doc.setFont("helvetica","bold"); doc.setFontSize(7.5);
    doc.text(label.toUpperCase(), LM, sy);
    S(RULE); doc.setLineWidth(0.5);
    doc.line(LM, sy + 3, PW - RM, sy + 3);
    return sy + 18;
  };

  const tRow = (label, value, ri, ry, valColor) => {
    F(ri % 2 === 0 ? ROW_A : WHITE);
    doc.rect(LM, ry - ROW_H + 6, CW, ROW_H, "F");
    TX(SUB); doc.setFont("helvetica","normal"); doc.setFontSize(9);
    doc.text(label, LM + 12, ry - 2);
    TX(valColor || DARK); doc.setFont("helvetica","bold"); doc.setFontSize(9);
    doc.text(value, PW - RM - 10, ry - 2, { align:"right" });
    return ry + ROW_H;
  };

  // ── booking details ───────────────────────────────────
  y = y + CARD_H + 22;
  y = sHead("Booking Details", y);

  const todayFmt = new Date().toLocaleDateString("en-KE",{day:"numeric",month:"long",year:"numeric"});
  [
    ["Order ID",        booking.id],
    ["Service",         booking.service],
    ["Property Type",   booking.propertyType || "Residential"],
    ["Address",         booking.address],
    ["Service Date",    booking.date],
    ["Time",            booking.time],
    ["Duration",        booking.duration],
    ["Payment Method",  booking.paymentMethod || "M-Pesa"],
    ["Payment Status",  "Paid"],
    ["Receipt Issued",  todayFmt],
  ].forEach(([lbl, val], i) => { y = tRow(lbl, val, i, y); });

  // ── professional ──────────────────────────────────────
  y += 18;
  y = sHead("Your Professional", y);
  const proName = booking.pro?.name || "Professional";
  [
    ["Name",           proName],
    ["Rating",         `${booking.pro?.rating?.toFixed(1) || "N/A"} / 5.0   (${booking.pro?.reviewCount || 0} reviews)`],
    ["Jobs Completed", String(booking.pro?.jobsCompleted || 0)],
    ["Verified",       "Identity Verified  |  Background Checked"],
  ].forEach(([lbl, val], i) => { y = tRow(lbl, val, i, y); });

  // ── payment summary ───────────────────────────────────
  y += 18;
  y = sHead("Payment Summary", y);
  const total = booking.price || 0;
  const platform = 500;
  const svcFee = total - platform;
  y = tRow("Service Fee",  `KES ${svcFee.toLocaleString("en-KE")}`, 0, y);
  y = tRow("Platform Fee", `KES ${platform.toLocaleString("en-KE")}`, 1, y);

  // total bar
  y += 6;
  F(GREEN); doc.roundedRect(LM, y, CW, 30, 6, 6, "F");
  TX(WHITE); doc.setFont("helvetica","bold"); doc.setFontSize(10);
  doc.text("Total Paid", LM + 14, y + 19);
  doc.setFontSize(13);
  doc.text(`KES ${total.toLocaleString("en-KE")}`, PW - RM - 14, y + 20, { align:"right" });
  y += 38;

  // ── guarantee band ────────────────────────────────────
  y += 12;
  F(GREEN_TINT); S(GREEN_BD); doc.setLineWidth(0.5);
  doc.roundedRect(LM, y, CW, 28, 5, 5, "FD");
  TX(GREEN_DARK); doc.setFont("helvetica","bold"); doc.setFontSize(8);
  doc.text("Protected by the Jirani Guarantee", PW / 2, y + 11, { align:"center" });
  TX(SUB); doc.setFont("helvetica","normal"); doc.setFontSize(7.5);
  doc.text("Dispute within 48 hours at support@jirani.co.ke", PW / 2, y + 22, { align:"center" });

  // ── footer ────────────────────────────────────────────
  const FY = PH - 38;
  F(RULE); doc.rect(0, FY, PW, 0.5, "F");
  TX(FAINT); doc.setFont("helvetica","normal"); doc.setFontSize(7.5);
  doc.text("Official receipt by Jirani Technologies Ltd. System-generated, valid without a signature.", PW/2, FY+14, { align:"center" });
  TX(GREEN); doc.setFont("helvetica","bold"); doc.setFontSize(7.5);
  doc.text("www.jirani.co.ke   |   support@jirani.co.ke   |   +254 700 000 000", PW/2, FY+26, { align:"center" });

  doc.save(`Jirani_Receipt_${booking.id}.pdf`);
}

/* ─────────────────────────────────────────────
   ATOMS
───────────────────────────────────────────── */
function Ava({ name, initials, avatar_url, size = 48, ring }) {
  const [err, setErr] = useState(false);
  const COLORS = ["#0F7B3E","#0369A1","#7C3AED","#B45309","#BE185D","#0E7490"];
  const c = COLORS[Math.abs((name?.charCodeAt(0)||0)+(name?.charCodeAt(1)||0)) % COLORS.length];
  const wrap = {
    width:size, height:size, borderRadius:"50%", flexShrink:0, overflow:"hidden",
    boxShadow: ring ? `0 0 0 3px #fff, 0 0 0 5px ${c}55` : "none",
  };
  if (avatar_url && !err)
    return <div style={wrap}><img src={avatar_url} alt={name}
      style={{ width:"100%", height:"100%", objectFit:"cover" }} onError={() => setErr(true)} /></div>;
  return <div style={{ ...wrap, background:c, display:"flex", alignItems:"center", justifyContent:"center",
    fontSize:Math.round(size*0.32), fontWeight:700, color:"#fff", fontFamily:T.font }}>
    {(initials||"?").slice(0,2).toUpperCase()}
  </div>;
}

function Stars({ rating, size = 13 }) {
  return <span style={{ display:"inline-flex", gap:2 }}>
    {[1,2,3,4,5].map(s => (
      <svg key={s} width={size} height={size} viewBox="0 0 20 20" fill={s<=Math.round(rating)?"#F59E0B":"#E5E7EB"}>
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
      </svg>
    ))}
  </span>;
}

function Pill({ status }) {
  const cfg = STATUS_CFG[status] || STATUS_CFG.confirmed;
  const live = ["on_the_way","in_progress","arrived"].includes(status);
  return (
    <span style={{ display:"inline-flex", alignItems:"center", gap:7,
      background:cfg.bg, border:`1px solid ${cfg.bd}`, borderRadius:T.rFull, padding:"6px 13px", flexShrink:0 }}>
      <span style={{ width:8, height:8, borderRadius:"50%", background:cfg.dot, flexShrink:0,
        animation:live?"pulseDot 2s ease-in-out infinite":"none",
        boxShadow:live?`0 0 0 3px ${cfg.bd}`:"none" }} />
      <span style={{ fontSize:12, fontWeight:700, color:cfg.color, fontFamily:T.font, letterSpacing:"0.01em" }}>
        {cfg.label}
      </span>
    </span>
  );
}

function Hr() { return <div style={{ height:1, background:T.borderFaint }} />; }

function Card({ children, style: sx={} }) {
  return <div style={{ background:T.card, borderRadius:T.r20, border:`1px solid ${T.border}`,
    boxShadow:T.s1, overflow:"hidden", ...sx }}>{children}</div>;
}

function SHead({ title, sub }) {
  return <div style={{ padding:"18px 22px 14px" }}>
    <p style={{ margin:0, fontSize:13, fontWeight:700, color:T.text, fontFamily:T.font, letterSpacing:"-0.01em" }}>{title}</p>
    {sub && <p style={{ margin:"2px 0 0", fontSize:11, color:T.faint, fontFamily:T.font }}>{sub}</p>}
  </div>;
}

function IRow({ icon, label, value, vc, last }) {
  return <>
    <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", padding:"12px 22px", gap:16 }}>
      <div style={{ display:"flex", alignItems:"center", gap:10, flexShrink:0 }}>
        <span style={{ color:T.faint, display:"flex" }}>{icon}</span>
        <span style={{ fontSize:13, color:T.sub, fontFamily:T.font }}>{label}</span>
      </div>
      <span style={{ fontSize:13, fontWeight:600, color:vc||T.text, fontFamily:T.font, textAlign:"right", maxWidth:220 }}>{value}</span>
    </div>
    {!last && <Hr />}
  </>;
}

/* ─────────────────────────────────────────────
   PROGRESS BAR
   • Completed step shows tick ✓
   • No "LIVE" badge
   • "On The Way" label
───────────────────────────────────────────── */
function ProgressBar({ status }) {
  const currentIdx  = getJourneyIndex(status);
  const isCancelled = status === "cancelled";

  return (
    <>
      <style>{`
        .pb-wrap { padding: 22px 20px 24px; }
        @media (min-width: 520px) {
          .pb-track      { display:flex; flex-direction:row; align-items:flex-start; }
          .pb-step       { flex:1; display:flex; flex-direction:column; align-items:center; position:relative; }
          .pb-dot-col    { display:flex; flex-direction:column; align-items:center; width:28px; }
          .pb-dot        { width:28px; height:28px; border-radius:50%; flex-shrink:0; display:flex; align-items:center; justify-content:center; }
          .pb-line-v     { display:none !important; }
          .pb-line-h     { display:block !important; }
          .pb-label-wrap { padding-bottom:0; }
          .pb-label      { font-size:11px; text-align:center; }
        }
        @media (max-width: 519px) {
          .pb-track      { display:flex; flex-direction:column; }
          .pb-step       { display:flex; flex-direction:row; align-items:stretch; gap:14px; }
          .pb-dot-col    { display:flex; flex-direction:column; align-items:center; width:28px; flex-shrink:0; }
          .pb-dot        { width:28px; height:28px; border-radius:50%; flex-shrink:0; display:flex; align-items:center; justify-content:center; }
          .pb-line-h     { display:none !important; }
          .pb-line-v     { display:block !important; width:2px; flex:1; min-height:16px; margin:3px 0; }
          .pb-label-wrap { padding-bottom:18px; display:flex; flex-direction:column; justify-content:center; }
          .pb-label      { font-size:13px; text-align:left; }
        }
      `}</style>

      <div className="pb-wrap">
        <div className="pb-track">
          {JOURNEY.map((step, idx) => {
            const isDone    = !isCancelled && idx < currentIdx;
            const isCurrent = !isCancelled && idx === currentIdx;
            const isFuture  = isCancelled  || idx > currentIdx;
            const isLast    = idx === JOURNEY.length - 1;
            const lineColor = isDone ? T.green : T.borderFaint;

            return (
              <div key={step.key} className="pb-step" style={{ position:"relative" }}>
                {!isLast && (
                  <div className="pb-line-h" style={{
                    position:"absolute", top:13,
                    left:"calc(50% + 15px)", right:"calc(-50% + 15px)",
                    height:2, background:lineColor, transition:"background 0.4s ease", pointerEvents:"none",
                  }} />
                )}
                <div className="pb-dot-col">
                  <div className="pb-dot" style={{
                    background: isDone||isCurrent ? T.green : T.card2,
                    border:`2px solid ${isDone||isCurrent ? T.green : T.border}`,
                    boxShadow: isCurrent ? `0 0 0 5px ${T.greenTint}` : "none",
                    transition:"all 0.3s ease",
                  }}>
                    {/* Done steps AND the completed step all show a tick */}
                    {(isDone || (isCurrent && step.key === "completed")) && (
                      <CheckCircle2 size={14} color="#fff" strokeWidth={2.5} />
                    )}
                    {isCurrent && step.key !== "completed" && (
                      <span style={{ width:10, height:10, borderRadius:"50%", background:"#fff",
                        animation:"innerPulse 2s ease-in-out infinite", display:"block" }} />
                    )}
                    {isFuture && (
                      <span style={{ width:7, height:7, borderRadius:"50%", background:T.ghost, display:"block" }} />
                    )}
                  </div>
                  {!isLast && (
                    <div className="pb-line-v" style={{
                      background: isDone ? T.green : T.borderFaint,
                      transition:"background 0.4s ease",
                    }} />
                  )}
                </div>
                <div className="pb-label-wrap">
                  <p className="pb-label" style={{
                    margin:0,
                    fontWeight: isCurrent ? 700 : isDone ? 600 : 400,
                    color: isFuture ? T.ghost : isCurrent ? T.text : T.sub,
                    fontFamily:T.font, letterSpacing:"-0.01em", transition:"color 0.3s ease",
                  }}>
                    {step.label}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}

/* ─────────────────────────────────────────────
   PROFESSIONAL CARD
───────────────────────────────────────────── */
function ProCard({ pro, navigate }) {
  return (
    <Card>
      <SHead title="Your Professional" />
      <Hr />
      <div style={{ padding:"20px 22px 18px" }}>
        <div style={{ display:"flex", alignItems:"center", gap:14, marginBottom:18 }}>
          <Ava name={pro.name} initials={pro.initials} avatar_url={pro.avatar_url} size={62} ring />
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:3 }}>
              <p style={{ margin:0, fontSize:16, fontWeight:700, color:T.text, fontFamily:T.font, letterSpacing:"-0.02em" }}>{pro.name}</p>
              {pro.verified && <ShieldCheck size={16} color={T.green} strokeWidth={2} />}
            </div>
            <div style={{ display:"flex", alignItems:"center", gap:5, marginBottom:4 }}>
              <Stars rating={pro.rating} />
              <span style={{ fontSize:13, fontWeight:700, color:T.text, fontFamily:T.font }}>{pro.rating.toFixed(1)}</span>
              <span style={{ fontSize:12, color:T.faint, fontFamily:T.font }}>({pro.reviewCount})</span>
            </div>
            <p style={{ margin:0, fontSize:12, color:T.sub, fontFamily:T.font }}>{pro.jobsCompleted} completed jobs</p>
          </div>
        </div>
        <div style={{ background:T.card2, borderRadius:T.r14, border:`1px solid ${T.border}`, padding:"14px 16px", marginBottom:16 }}>
          {[
            { label:"Professional since", value:pro.memberSince },
            { label:"Specializes in",     value:pro.specialization },
            { label:"Response time",      value:pro.responseTime },
          ].map((row, i, arr) => (
            <div key={row.label} style={{
              display:"flex", justifyContent:"space-between", alignItems:"center",
              paddingBottom:i<arr.length-1?10:0, marginBottom:i<arr.length-1?10:0,
              borderBottom:i<arr.length-1?`1px solid ${T.borderFaint}`:"none",
            }}>
              <span style={{ fontSize:12, color:T.faint, fontFamily:T.font }}>{row.label}</span>
              <span style={{ fontSize:12, fontWeight:600, color:T.textMid, fontFamily:T.font }}>{row.value}</span>
            </div>
          ))}
        </div>
        <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:18 }}>
          {["Identity Verified","Background Checked","Top Rated"].map(b => (
            <span key={b} style={{ display:"inline-flex", alignItems:"center", gap:5,
              background:T.greenTint, border:`1px solid ${T.greenBd}`,
              borderRadius:T.rFull, padding:"4px 10px",
              fontSize:11, fontWeight:600, color:T.green, fontFamily:T.font }}>
              <ShieldCheck size={11} strokeWidth={2}/> {b}
            </span>
          ))}
        </div>
        <div style={{ display:"flex", gap:8 }}>
          {[
            { icon:<MessageSquare size={16} strokeWidth={1.75}/>, label:"Message", fn:() => navigate("/messages") },
            { icon:<Phone size={16} strokeWidth={1.75}/>,         label:"Call",    fn:() => {} },
            { icon:<User size={16} strokeWidth={1.75}/>,          label:"Profile", fn:() => {} },
          ].map(btn => <ProBtn key={btn.label} {...btn} />)}
        </div>
      </div>
    </Card>
  );
}

function ProBtn({ icon, label, fn }) {
  const [h, setH] = useState(false);
  return (
    <button onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)} onClick={fn}
      style={{ flex:1, padding:"11px 8px", background:h?T.card2:T.card,
        border:`1px solid ${h?T.greenBd:T.border}`, borderRadius:T.r12, cursor:"pointer",
        fontFamily:T.font, fontSize:13, fontWeight:600, color:h?T.green:T.textMid,
        display:"flex", alignItems:"center", justifyContent:"center", gap:6, transition:"all 0.15s" }}>
      {icon} {label}
    </button>
  );
}

/* ─────────────────────────────────────────────
   TRUST SIGNALS
───────────────────────────────────────────── */
function TrustSection() {
  const items = [
    { icon:<ShieldCheck size={18} color={T.green} strokeWidth={1.75}/>, bg:T.greenTint, title:"Jirani Guarantee",     sub:"Every service is backed by our satisfaction guarantee." },
    { icon:<Award size={18} color="#7C3AED" strokeWidth={1.75}/>,       bg:"#F5F3FF",   title:"Verified Professional", sub:"Identity verified, background-checked, customer reviewed." },
    { icon:<CreditCard size={18} color={T.blue} strokeWidth={1.75}/>,   bg:T.blueTint,  title:"Secure Payment",        sub:"Your payment is held safely until service completion." },
  ];
  return (
    <Card>
      <SHead title="Your Booking is Protected" />
      <Hr />
      {items.map((item, i) => (
        <React.Fragment key={item.title}>
          <div style={{ display:"flex", alignItems:"flex-start", gap:14, padding:"14px 22px" }}>
            <div style={{ width:36, height:36, borderRadius:T.r10, background:item.bg,
              display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>{item.icon}</div>
            <div>
              <p style={{ margin:0, fontSize:13, fontWeight:700, color:T.text, fontFamily:T.font }}>{item.title}</p>
              <p style={{ margin:"3px 0 0", fontSize:12, color:T.sub, fontFamily:T.font, lineHeight:1.5 }}>{item.sub}</p>
            </div>
          </div>
          {i < items.length-1 && <Hr />}
        </React.Fragment>
      ))}
    </Card>
  );
}

/* ─────────────────────────────────────────────
   ROTATING INFO CARD
───────────────────────────────────────────── */
function InfoCard({ status }) {
  const [idx, setIdx] = useState(0);
  const cards = INFO_CARDS_BY_STATUS[status] || INFO_CARDS_BY_STATUS.default;
  const c = cards[idx];
  const iconMap = {
    zap:    <Zap size={17} color="#D97706" strokeWidth={1.75}/>,
    shield: <ShieldCheck size={17} color={T.green} strokeWidth={1.75}/>,
    award:  <Award size={17} color="#7C3AED" strokeWidth={1.75}/>,
  };
  return (
    <div style={{ background:c.bg, border:`1px solid ${c.bd}`, borderRadius:T.r20, padding:"18px 20px" }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12 }}>
        <div style={{ display:"flex", alignItems:"center", gap:9 }}>
          <div style={{ width:32, height:32, borderRadius:T.r10, background:"rgba(255,255,255,0.65)",
            display:"flex", alignItems:"center", justifyContent:"center" }}>{iconMap[c.icon]}</div>
          <p style={{ margin:0, fontSize:13, fontWeight:700, color:c.tc, fontFamily:T.font }}>{c.title}</p>
        </div>
        <div style={{ display:"flex", gap:5 }}>
          {cards.map((_, i) => (
            <button key={i} onClick={() => setIdx(i)} style={{
              width:i===idx?18:6, height:6, borderRadius:T.rFull, padding:0, border:"none", cursor:"pointer",
              background:i===idx?c.tc:`${c.tc}44`, transition:"all 0.25s ease",
            }} />
          ))}
        </div>
      </div>
      <ul style={{ margin:0, padding:0, listStyle:"none" }}>
        {c.lines.map(line => (
          <li key={line} style={{ display:"flex", alignItems:"flex-start", gap:8,
            fontSize:12, color:c.tc, fontFamily:T.font, marginBottom:6, opacity:0.8, lineHeight:1.55 }}>
            <span style={{ marginTop:5, width:4, height:4, borderRadius:"50%", background:c.tc, flexShrink:0 }} />
            {line}
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ─────────────────────────────────────────────
   COMPLETED SCREEN
   • No "Service Complete / Your Service Is Complete" headers
   • Opens with the personalised message
   • Download Receipt triggers real PDF download
───────────────────────────────────────────── */
function CompletedScreen({ booking, navigate }) {
  const first = booking.pro?.name?.split(" ")[0] || "Your professional";
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    setDownloading(true);
    try { await downloadReceipt(booking); }
    catch (e) { console.error("Receipt error", e); }
    finally { setDownloading(false); }
  };

  return (
    <div style={{ background:T.green, borderRadius:T.r20, padding:"28px 24px 24px",
      marginBottom:12, position:"relative", overflow:"hidden" }}>
      <div style={{ position:"absolute", top:-30, right:-30, width:130, height:130,
        borderRadius:"50%", background:"rgba(255,255,255,0.06)" }} />
      <div style={{ position:"absolute", bottom:-20, left:-20, width:90, height:90,
        borderRadius:"50%", background:"rgba(255,255,255,0.04)" }} />
      <div style={{ position:"relative" }}>
        <p style={{ margin:"0 0 22px", fontSize:14, color:"rgba(255,255,255,0.85)",
          fontFamily:T.font, textAlign:"center", lineHeight:1.7, fontWeight:500 }}>
          {first} has successfully completed your {booking.service}. We hope everything met your expectations.
        </p>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
          {[
            { icon:<Star size={16}/>,   label:"Leave a Review",   primary:true,  fn:() => navigate(`/review/${booking.id}`) },
            { icon:<RefreshCw size={16}/>, label:"Book Again",       primary:false, fn:() => navigate("/") },
            { icon:<Download size={16}/>, label: downloading ? "Generating…" : "Download Receipt", primary:false, fn: handleDownload },
            { icon:<Gift size={16}/>,     label:"Tip Professional", primary:false, fn:() => {} },
          ].map(btn => <CompletedBtn key={btn.label} {...btn} />)}
        </div>
      </div>
    </div>
  );
}

function CompletedBtn({ icon, label, primary, fn }) {
  const [h, setH] = useState(false);
  return (
    <button onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)} onClick={fn} style={{
      padding:"11px 8px",
      background:primary?(h?"#fff":"rgba(255,255,255,0.92)"):(h?"rgba(255,255,255,0.2)":"rgba(255,255,255,0.1)"),
      border:primary?"none":"1px solid rgba(255,255,255,0.25)",
      borderRadius:T.r12, cursor:"pointer", fontFamily:T.font,
      fontSize:12, fontWeight:700, color:primary?T.green:"#fff",
      display:"flex", alignItems:"center", justifyContent:"center", gap:6, transition:"all 0.18s",
    }}>
      {icon} {label}
    </button>
  );
}

/* ─────────────────────────────────────────────
   SUPPORT SECTION
   • "arrived" status: cancel removed → "Report a Problem" + "I Have a Question"
   • in_progress: same — no cancel
   • confirmed / on_the_way: cancel still available (pro hasn't arrived yet)
───────────────────────────────────────────── */
function SupportBtn({ icon, label, sub, color, danger, onClick }) {
  const [h, setH] = useState(false);
  return (
    <button onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)} onClick={onClick} style={{
      width:"100%", display:"flex", alignItems:"center", justifyContent:"space-between",
      padding:"14px 22px", background:danger&&h?T.redTint:h?T.card2:"none",
      border:"none", cursor:"pointer", fontFamily:T.font, textAlign:"left", transition:"background 0.15s",
    }}>
      <div style={{ display:"flex", alignItems:"center", gap:12 }}>
        <span style={{ color }}>{icon}</span>
        <div>
          <p style={{ margin:0, fontSize:13, fontWeight:600, color, fontFamily:T.font }}>{label}</p>
          <p style={{ margin:"1px 0 0", fontSize:11, color:T.faint, fontFamily:T.font }}>{sub}</p>
        </div>
      </div>
      <ChevronRight size={15} color={T.ghost} />
    </button>
  );
}

function SupportCard({ status, isCompleted, isCancelled, onCancelClick, onRescheduleClick }) {
  // Once pro has arrived or is working → no cancel option
  const proIsPresent = ["arrived","in_progress"].includes(status);
  const canCancel    = !isCompleted && !isCancelled && !proIsPresent;

  return (
    <Card style={{ marginBottom:12 }}>
      <SHead title="Need Help?" />
      <Hr />
      <SupportBtn
        icon={<HelpCircle size={16} strokeWidth={1.75}/>}
        label="Contact Support" sub="Get help from the Jirani team"
        color={T.sub} danger={false} />
      <Hr />
      <SupportBtn
        icon={<AlertCircle size={16} strokeWidth={1.75}/>}
        label="Report a Problem" sub="Something wrong with your booking"
        color={T.sub} danger={false} />
      {proIsPresent && !isCompleted && (
        <>
          <Hr />
          <SupportBtn
            icon={<MessageSquare size={16} strokeWidth={1.75}/>}
            label="Message Your Professional" sub="Ask a question or share an update"
            color={T.blue} danger={false} />
          <Hr />
          <SupportBtn
            icon={<Phone size={16} strokeWidth={1.75}/>}
            label="Call Your Professional" sub="Reach them directly if needed"
            color={T.sub} danger={false} />
        </>
      )}
      {canCancel && (
        <>
          <Hr />
          <SupportBtn
            icon={<RefreshCw size={16} strokeWidth={1.75}/>}
            label="Reschedule Booking" sub="Request a different date or time"
            color={T.blue} onClick={onRescheduleClick} />
          <Hr />
          <SupportBtn
            icon={<X size={16} strokeWidth={1.75}/>}
            label="Cancel Booking" sub="Cancel before the professional arrives"
            color={T.red} danger onClick={onCancelClick} />
        </>
      )}
    </Card>
  );
}

/* ─────────────────────────────────────────────
   BOTTOM ACTION BAR
───────────────────────────────────────────── */
function BottomBar({ status, navigate, onCancelClick, onRescheduleClick, onMessageClick }) {
  const sets = {
    confirmed:    [{ icon:<MessageSquare size={18}/>, label:"Message Pro",    v:"info",  fn:onMessageClick },    { icon:<X size={18}/>,          label:"Cancel",   v:"danger", fn:onCancelClick }],
    pro_assigned: [{ icon:<MessageSquare size={18}/>, label:"Message Pro",    v:"info",  fn:onMessageClick },    { icon:<X size={18}/>,          label:"Cancel",   v:"danger", fn:onCancelClick }],
    accepted:     [{ icon:<MessageSquare size={18}/>, label:"Message Pro",    v:"info",  fn:onMessageClick },    { icon:<X size={18}/>,          label:"Cancel",   v:"danger", fn:onCancelClick }],
    on_the_way:   [{ icon:<MessageSquare size={18}/>, label:"Message",        v:"info",  fn:onMessageClick },    { icon:<Phone size={18}/>,      label:"Call Pro", v:"ghost",  fn:()=>{} }],
    arrived:      [{ icon:<MessageSquare size={18}/>, label:"Message",        v:"info",  fn:onMessageClick },    { icon:<Phone size={18}/>,      label:"Call Pro", v:"ghost",  fn:()=>{} }],
    in_progress:  [{ icon:<MessageSquare size={18}/>, label:"Message",        v:"info",  fn:onMessageClick },    { icon:<HelpCircle size={18}/>, label:"Support",  v:"ghost",  fn:()=>{} }],
    completed:    null,
    cancelled:    [{ icon:<HelpCircle size={18}/>,    label:"Contact Support",v:"ghost", fn:()=>{} }],
  };
  const actions = sets[status];
  if (!actions) return null;
  return (
    <div style={{ position:"fixed", bottom:0, left:0, right:0, zIndex:100,
      background:"rgba(255,255,255,0.96)", backdropFilter:"blur(14px)", WebkitBackdropFilter:"blur(14px)",
      borderTop:`1px solid ${T.borderFaint}`, padding:"12px 20px 28px",
      boxShadow:"0 -4px 20px rgba(0,0,0,0.06)" }}>
      <div style={{ maxWidth:600, margin:"0 auto", display:"flex", gap:10 }}>
        {actions.map((a,i) => <BarBtn key={i} {...a} />)}
      </div>
    </div>
  );
}

function BarBtn({ icon, label, v, fn }) {
  const [h, setH] = useState(false);
  const S = {
    primary: { bg:h?T.greenDark:T.green,   color:"#fff",    border:`1px solid ${T.green}` },
    info:    { bg:h?T.blueTint:T.card,      color:T.blue,    border:`1px solid ${h?T.blueBd:T.border}` },
    ghost:   { bg:h?T.card2:T.card,         color:T.textMid, border:`1px solid ${T.border}` },
    danger:  { bg:h?T.redTint:T.card,       color:T.red,     border:`1px solid ${h?T.redBd:T.border}` },
  };
  const s = S[v]||S.ghost;
  return (
    <button onClick={fn} onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)} style={{
      flex:1, padding:"13px 8px", background:s.bg, color:s.color, border:s.border,
      borderRadius:T.r14, cursor:"pointer", fontFamily:T.font, fontSize:13, fontWeight:600,
      display:"flex", alignItems:"center", justifyContent:"center", gap:8, transition:"all 0.18s ease",
    }}>
      {icon} {label}
    </button>
  );
}

/* ─────────────────────────────────────────────
   DEMO SWITCHER
───────────────────────────────────────────── */
function DemoSwitcher({ status, onChange }) {
  const [open, setOpen] = useState(false);
  const opts = ["confirmed","on_the_way","arrived","in_progress","completed","cancelled"];
  return (
    <div style={{ position:"fixed", bottom:88, right:20, zIndex:999 }}>
      {open && (
        <div style={{ background:"#111827", borderRadius:T.r16, padding:"14px 16px",
          boxShadow:T.s3, marginBottom:8, minWidth:160 }}>
          <p style={{ margin:"0 0 10px", fontSize:10, fontWeight:700, color:"#6B7280",
            fontFamily:T.font, textTransform:"uppercase", letterSpacing:"0.1em" }}>Simulate status</p>
          <div style={{ display:"flex", flexDirection:"column", gap:5 }}>
            {opts.map(s => (
              <button key={s} onClick={() => { onChange(s); setOpen(false); }} style={{
                padding:"6px 12px", borderRadius:T.r8, textAlign:"left",
                background:s===status?T.green:"#1F2937",
                border:`1px solid ${s===status?T.green:"#374151"}`,
                color:s===status?"#fff":"#9CA3AF",
                fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:T.font,
              }}>
                {STATUS_CFG[s]?.label||s}
              </button>
            ))}
          </div>
        </div>
      )}
      <button onClick={() => setOpen(o=>!o)} style={{
        width:44, height:44, borderRadius:"50%", background:"#111827", border:"none",
        display:"flex", alignItems:"center", justifyContent:"center",
        cursor:"pointer", boxShadow:T.s3, fontSize:18,
      }}>⚡</button>
    </div>
  );
}

/* ─────────────────────────────────────────────
   MAIN PAGE
───────────────────────────────────────────── */
export default function OrderTrackingPage() {
  const navigate   = useNavigate();
  const { open: openLogin } = useLoginModal();
  const { id }     = useParams();
  const [booking,  setBooking]  = useState(null);
  const [status,   setStatus]   = useState("on_the_way");
  const [loading,  setLoading]  = useState(true);
  const channelRef = useRef(null);
  const [showCancel, setShowCancel] = useState(false);
  const [showReschedule, setShowReschedule] = useState(false);

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const { data: sd } = await supabase.auth.getSession();
        if (!sd?.session) { openLogin(); return; }

        if (!id) { navigate("/bookings"); return; }

        const { data, error } = await supabase
          .from("job_requests")
          .select(`id, status, description, scheduled_time,
            location, estimated_price, notes, created_at,
            payment_status,
            pro_profiles!pro_id (
              id, avg_rating, review_count, completed_jobs,
              response_rate, verified, member_since, title, response_minutes,
              profiles!user_id ( full_name, avatar_url, phone )
            )`)
          .eq("id", id)
          .single();

        if (error) throw error;

        const p  = data.pro_profiles;
        const pn = p?.profiles?.full_name || "Professional";
        const scheduled = data.scheduled_time ? new Date(data.scheduled_time) : null;

        setBooking({
          id:data.id, status:data.status, service:data.description,
          date: scheduled ? scheduled.toLocaleDateString("en-KE",{weekday:"long",day:"numeric",month:"long"}) : "—",
          time: scheduled ? scheduled.toLocaleTimeString("en-KE",{hour:"2-digit",minute:"2-digit"}) : "—",
          duration: "—",
          address:data.location, propertyType:"—",
          price:data.estimated_price, currency:"KES",
          paymentMethod:"M-Pesa",
          paymentStatus:data.payment_status||"unpaid",
          specialNotes:data.notes,
          pro:{
            id:p?.id, name:pn,
            initials:pn.split(" ").map(n=>n[0]).join("").slice(0,2).toUpperCase(),
            avatar_url:p?.profiles?.avatar_url, rating:p?.avg_rating||0,
            reviewCount:p?.review_count||0, jobsCompleted:p?.completed_jobs||0,
            yearsExperience:0, responseRate:p?.response_rate||0,
            verified:p?.verified||false, phone:p?.profiles?.phone||"",
            memberSince:p?.member_since||"2022",
            specialization:p?.title||"Residential cleaning",
            responseTime: p?.response_minutes ? `Under ${p.response_minutes} minutes` : "Under 5 minutes",
          },
        });
        setStatus(data.status);
      } catch (err) {
        console.error("Booking load failed:", err);
        navigate("/bookings");
      }
      finally { setLoading(false); }
    }
    load();
  }, [id, navigate]);

  useEffect(() => {
    if (!booking?.id) return;
    channelRef.current = supabase
      .channel(`tracking-${booking.id}`)
      .on("postgres_changes",
        { event:"UPDATE", schema:"public", table:"job_requests", filter:`id=eq.${booking.id}` },
        (payload) => {
          const ns = payload.new?.status;
          if (ns && STATUS_CFG[ns]) { setStatus(ns); setBooking(p => p?{...p,status:ns}:p); }
        })
      .subscribe();
    return () => { if (channelRef.current) supabase.removeChannel(channelRef.current); };
  }, [booking?.id]);

  if (loading) return (
    <div style={{ minHeight:"100vh", background:T.pageBg, display:"flex",
      alignItems:"center", justifyContent:"center", fontFamily:T.font }}>
      <div style={{ textAlign:"center" }}>
        <Loader2 size={30} color={T.green} style={{ animation:"spin 0.9s linear infinite", marginBottom:10 }} />
        <p style={{ fontSize:13, color:T.sub, fontFamily:T.font, margin:0 }}>Loading your booking…</p>
      </div>
    </div>
  );

  if (!booking) return null;

  const isCompleted = status === "completed";
  const isCancelled = status === "cancelled";

  return (
    <div style={{ minHeight:"100vh", background:T.pageBg, fontFamily:T.font }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
        * { box-sizing: border-box; }
        @keyframes spin       { to{transform:rotate(360deg)} }
        @keyframes pulseDot   { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.5;transform:scale(1.5)} }
        @keyframes innerPulse { 0%,100%{opacity:1} 50%{opacity:.35} }
        @keyframes fadeUp     { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
        button:focus-visible  { outline:2px solid ${T.green}; outline-offset:2px; }
      `}</style>

      <main style={{ maxWidth:600, margin:"0 auto", padding:"16px 0 130px", animation:"fadeUp 0.35s ease-out" }}>
        <div style={{ padding:"0 0" }}>

          {/* COMPLETED CELEBRATION */}
          {isCompleted && <CompletedScreen booking={booking} navigate={navigate} />}

          {/* PROGRESS BAR */}
          {!isCancelled && (
            <Card style={{ marginBottom:12 }}>
              <SHead title="Service Journey" sub="Your booking, step by step" />
              <Hr />
              <ProgressBar status={status} />
            </Card>
          )}

          {/* PROFESSIONAL */}
          {booking.pro?.name && !isCancelled && (
            <div style={{ marginBottom:12 }}>
              <ProCard pro={booking.pro} navigate={navigate} />
            </div>
          )}

          {/* ROTATING INFO */}
          {!isCompleted && !isCancelled && (
            <div style={{ marginBottom:12 }}><InfoCard status={status} /></div>
          )}

          {/* BOOKING SUMMARY */}
          <Card style={{ marginBottom:12 }}>
            <SHead title="Booking Summary" />
            <Hr />
            <IRow icon={<FileText size={15} strokeWidth={1.75}/>}   label="Service"  value={booking.service} />
            <IRow icon={<Calendar size={15} strokeWidth={1.75}/>}   label="Date"     value={booking.date} />
            <IRow icon={<Clock size={15} strokeWidth={1.75}/>}      label="Time"     value={booking.time} />
            <IRow icon={<Clock size={15} strokeWidth={1.75}/>}      label="Duration" value={booking.duration} />
            <IRow icon={<MapPin size={15} strokeWidth={1.75}/>}     label="Address"  value={booking.address} />
            <IRow icon={<CreditCard size={15} strokeWidth={1.75}/>} label="Payment"
              value={`${booking.currency} ${booking.price?.toLocaleString("en-KE")}`}
              vc={T.green} />
            <Hr />
            <div style={{ padding:"16px 22px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
              <p style={{ margin:0, fontSize:14, fontWeight:700, color:T.text, fontFamily:T.font }}>Total Paid</p>
              <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                <span style={{ background:T.greenTint, border:`1px solid ${T.greenBd}`, borderRadius:T.rFull,
                  padding:"2px 9px", fontSize:10, fontWeight:700, color:T.green,
                  fontFamily:T.font, textTransform:"uppercase" }}>Paid</span>
                <p style={{ margin:0, fontSize:20, fontWeight:800, color:T.text,
                  fontFamily:T.font, letterSpacing:"-0.03em" }}>
                  {booking.currency} {booking.price?.toLocaleString("en-KE")}
                </p>
              </div>
            </div>
          </Card>

          {/* NOTES */}
          {booking.specialNotes && (
            <Card style={{ marginBottom:12 }}>
              <SHead title="Service Notes" sub="Shared with your professional" />
              <Hr />
              <div style={{ padding:"16px 22px" }}>
                <p style={{ margin:0, fontSize:13, color:T.textMid, fontFamily:T.font, lineHeight:1.65, fontStyle:"italic" }}>
                  "{booking.specialNotes}"
                </p>
              </div>
            </Card>
          )}

          {/* PHOTOS */}
          <Card style={{ marginBottom:12 }}>
            <SHead title="Service Photos" />
            <Hr />
            <div style={{ padding:"16px 22px" }}>
              <div style={{ background:T.card2, borderRadius:T.r14, border:`1px dashed ${T.border}`,
                padding:"28px 16px", textAlign:"center",
                display:"flex", flexDirection:"column", alignItems:"center", gap:8 }}>
                <Camera size={22} color={T.ghost} strokeWidth={1.5} />
                <p style={{ margin:0, fontSize:13, fontWeight:600, color:T.faint, fontFamily:T.font }}>
                  {isCompleted ? "No photos were added" : "Photos will appear here automatically"}
                </p>
                <p style={{ margin:0, fontSize:11, color:T.ghost, fontFamily:T.font }}>
                  Your professional may upload before, during, and after photos
                </p>
              </div>
            </div>
          </Card>

          {/* TRUST */}
          <div style={{ marginBottom:12 }}><TrustSection /></div>

          {/* SUPPORT — context-aware, no cancel once pro has arrived */}
          <SupportCard status={status} isCompleted={isCompleted} isCancelled={isCancelled} onCancelClick={() => setShowCancel(true)} onRescheduleClick={() => setShowReschedule(true)} />

        </div>
      </main>

      <BottomBar
        status={status}
        navigate={navigate}
        onCancelClick={() => setShowCancel(true)}
        onRescheduleClick={() => setShowReschedule(true)}
        onMessageClick={() => navigate("/messages")}
      />

      {showCancel && (
        <CancelBookingModal
          booking={booking}
          onClose={() => setShowCancel(false)}
          onCancelled={(updated) => {
            setStatus("cancelled");
            setBooking(prev => prev ? { ...prev, status: "cancelled", notes: updated.notes } : prev);
            setShowCancel(false);
          }}
        />
      )}
      {showReschedule && (
        <RescheduleBookingModal
          booking={booking}
          onClose={() => setShowReschedule(false)}
          onRequested={(updated) => {
            setStatus("reschedule_requested");
            setBooking(prev => prev ? { ...prev, status: "reschedule_requested", requested_scheduled_time: updated.requested_scheduled_time, notes: updated.notes } : prev);
            setShowReschedule(false);
          }}
        />
      )}

      {process.env.NODE_ENV === 'development' && (
        <DemoSwitcher status={status} onChange={setStatus} />
      )}
    </div>
  );
}